/**
 * Gera UUID v5 (SHA-1) a partir de um nome e namespace, conforme RFC 4122.
 */

const NAMESPACE_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

function uuidToBytes(uuid: string): Uint8Array {
  if (!NAMESPACE_REGEX.test(uuid)) {
    throw new Error(`Invalid UUID namespace: ${uuid}`);
  }
  const hex = uuid.replace(/-/g, "");
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function bytesToUuid(bytes: Uint8Array): string {
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return [
    hex.substring(0, 8),
    hex.substring(8, 12),
    hex.substring(12, 16),
    hex.substring(16, 20),
    hex.substring(20, 32),
  ].join("-");
}

/**
 * Gera UUID v5 a partir de um nome e namespace.
 *
 * @param name Nome/string de entrada.
 * @param namespace UUID do namespace (ex.: DNS namespace).
 * @returns UUID v5 gerado.
 */
export async function uuidv5(name: string, namespace: string): Promise<string> {
  const namespaceBytes = uuidToBytes(namespace);
  const nameBytes = new TextEncoder().encode(name);
  const combined = new Uint8Array(namespaceBytes.length + nameBytes.length);
  combined.set(namespaceBytes, 0);
  combined.set(nameBytes, namespaceBytes.length);

  const hashBuffer = await crypto.subtle.digest("SHA-1", combined);
  const hash = new Uint8Array(hashBuffer);
  const uuidBytes = hash.slice(0, 16);

  // version = 5 (0101)
  uuidBytes[6] = (uuidBytes[6] & 0x0f) | 0x50;
  // variant = 10xxxxxx
  uuidBytes[8] = (uuidBytes[8] & 0x3f) | 0x80;

  return bytesToUuid(uuidBytes);
}

/**
 * Gera um hash SHA-256 (hex) de uma string.
 */
export async function sha256Hex(input: string): Promise<string> {
  const buffer = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input),
  );
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
